import * as React from "react";

import connectToList from "../../store/list";

import GenButton from "../Gen/Button";
import GenControlsBar from "../Gen/ControlsBar";
import GenModal from "../Gen/Modal";
import GenMore from "../Gen/More";
import GenSearchBox from "../Gen/SearchBox";
import GenSelectBar from "../Gen/SelectBar";
import GenSelectFilter from "../Gen/SelectFilter";
import GenTableDisplay from "../Gen/TableDisplay";

interface GroupRoleDefinitionUserExpirationModalProps extends ConnectedList<GroupRoleDefinitionUserSnakeModel> {
  closeModal: () => void;
}

function parseDateStrings(dayString: string, monthString: string, yearString: string) {
  const day = parseInt(dayString);
  const month = parseInt(monthString);
  const year = parseInt(yearString);

  if (!month || month > 12 || !day || day > 31 || !year || year > 99) {
      return(NaN);
  } else {
    const date = Date.parse(`${year + 2000}-${month.toString().padStart(2, "0")}-${day.toString().padStart(2, "0")}`);
    if (date > Date.now()) {
      return(date);
    } else {
      return(NaN);
    }
  }
}

function GroupRoleDefinitionUserExpirationModal(props: GroupRoleDefinitionUserExpirationModalProps) {
  const {
    selected,
    resetListSelected,
    reloadList,
    closeModal,
  } = props;

  const [exiprationProcessing, setExpirationProcessing] = React.useState(false);
  const [expirationMode, setExpirationMode] = React.useState<"past" | "never" | "date">("past");
  const [dayString, setDayString] = React.useState("");
  const [monthString, setMonthString] = React.useState("");
  const [yearString, setYearString] = React.useState("");

  const handleSetExpirationSubmit = React.useCallback(() => {
    if (selected.length === 0) {
      alert("Nothing Selected! You must select at least one item to lend.");
      return(null);
    }

    const data = expirationMode === "date" ? {
      group_role_definition_user_ids: selected.map((grdu) => grdu.id),
      expiration_mode: expirationMode,
      expiration_date: parseDateStrings(dayString, monthString, yearString),
    } : {
      group_role_definition_user_ids: selected.map((grdu) => grdu.id),
      expiration_mode: expirationMode,
    };

    setExpirationProcessing(true);

    fetch("/group_role_definition_users/update_expiration_batch", {
      method: "PATCH",
      body: JSON.stringify(data),
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    })
      .then((response) => {
        if (!response.ok) { throw new Error(`${response.status} response`); }

        return response.json();
      })
      .then(() => {
        setExpirationProcessing(false);
        resetListSelected();
        reloadList();
        closeModal();
        alert("Members Updated!");
      })
      .catch(() => {
        setExpirationProcessing(false);
        alert("Error: Updating Members!");
      });
  }, [selected, resetListSelected, reloadList, dayString, monthString, yearString, expirationMode]);

  const isValidDate = !!parseDateStrings(dayString, monthString, yearString);

  return (
    <GenModal open>
      <div className="gen-modal-panel">
        <h1>Set Expiration</h1>
        <div>
          <form action="">
            <div>
              <label><input type="radio" value="past" checked={expirationMode === "past"} onChange={() => setExpirationMode("past")}/>Expire Now</label>
            </div>
            <div>
              <label><input type="radio" value="never" checked={expirationMode === "never"} onChange={() => setExpirationMode("never")}/>Never Expire</label>
            </div>
            <div>
              <label><input type="radio" value="date" checked={expirationMode === "date"} onChange={() => setExpirationMode("date")}/>Expire On Date...</label>
            </div>
            <div>
              <input
                type="text" name="month" placeholder="mm"
                disabled={"date" != expirationMode}
                style={{width: `${45}px`, display: "inline-block"}}
                value={monthString}
                onChange={(e) => setMonthString(e.target.value)}
              />/
              <input
                type="text" name="day" placeholder="dd"
                disabled={"date" != expirationMode}
                style={{width: `${45}px`, display: "inline-block"}}
                value={dayString}
                onChange={(e) => setDayString(e.target.value)}
              />/
              <input
                type="text" name="year" placeholder="yy"
                disabled={"date" != expirationMode}
                style={{width: `${45}px`, display: "inline-block"}}
                value={yearString}
                onChange={(e) => setYearString(e.target.value)}
              />
              {expirationMode === "date" && !isValidDate && <p>Invalid Date: Date must be valid and in the future</p>}
            </div>
          </form>
          <div className="gen-button-left-group">
            <GenButton name="Set Expiration" highlighted small disabled={expirationMode === "date" && !isValidDate} onClick={handleSetExpirationSubmit}/>
            <GenButton name="Cancel" highlighted small onClick={closeModal}/>
          </div>
        </div>
      </div>
    </GenModal>
  );
}

interface GroupRoleDefinitionUserTableProps {
  groupRoleDefinitionUsers: ConnectedList<GroupRoleDefinitionUserSnakeModel>;
  is_admin: boolean;
  role_definition_names: Array<string>;
}

function GroupRoleDefinitionUserTable(props: GroupRoleDefinitionUserTableProps) {
  const {
    groupRoleDefinitionUsers,
    is_admin,
    role_definition_names,
  } = props;

  const [destroyProcessing, setDestroyProcessing] = React.useState(false);
  const [isExpirationModalOpen, setIsExpirationModalOpen] = React.useState(false);

  React.useEffect(() => {
    groupRoleDefinitionUsers.mountList("/group_role_definition_users");
  }, []);

  const handleDestroySelectedGroupRoleDefinitions = () => {
    if (groupRoleDefinitionUsers.selected.length === 0) {
      alert("Nothing Selected! You must select at least one item to lend.");
      return;
    }

    const removeConfirm = confirm(`Are you sure you want to remove ${groupRoleDefinitionUsers.selected.length} member(s)?`);

    if (!removeConfirm) {
      return;
    }

    const data = {groupRoleDefinitionUserIds: groupRoleDefinitionUsers.selected.map((grdu) => grdu.id)};

    setDestroyProcessing(true);

    fetch("/group_role_definition_users/destroy_batch", {
      method: "DELETE",
      body: JSON.stringify(data),
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    })
      .then((response) => {
        if (!response.ok) { throw new Error(`${response.status} response`); }

        return response.json();
      })
      .then(() => {
        setDestroyProcessing(false);
        groupRoleDefinitionUsers.resetListSelected();
        groupRoleDefinitionUsers.reloadList();
        alert("Members Removed!");
      })
      .catch(() => {
        setDestroyProcessing(false);
        alert("Error: Removing Members!");
      });
  };

  return(
    <div>
      <GenSelectBar
        {...groupRoleDefinitionUsers}
        selectExtraButtons={(
          <>
            {is_admin && (
              <GenButton
                name="Set Expiration For Selected"
                highlighted
                small
                disabled={groupRoleDefinitionUsers.selected.length === 0}
                onClick={() => (setIsExpirationModalOpen(!isExpirationModalOpen))}
              />
            )}
            <GenButton
              name="Remove Selected Members"
              highlighted
              loading={destroyProcessing}
              small
              disabled={groupRoleDefinitionUsers.selected.length === 0}
              onClick={handleDestroySelectedGroupRoleDefinitions}
            />
          </>
        )}
      />
      <GenControlsBar>
        <GenSearchBox
          {...groupRoleDefinitionUsers}
        />

        <div className="flex gap-2">
          <GenSelectFilter
            {...groupRoleDefinitionUsers}
            filterKey="Role"
            label="Role"
            options={role_definition_names}
            filterGenerator={(option) => ["=", "role_definitions.name", option]}
          />
          <GenSelectFilter
            {...groupRoleDefinitionUsers}
            filterKey="Subscription Status"
            label="Subscription Status"
            type="Explicit"
            options={[
              {name: "Invited", value: ["is_null", "group_role_definition_users.subscription_end"]},
              {name: "Active", value: [">", "group_role_definition_users.subscription_end", (new Date().toISOString())]},
              {name: "Expired", value: ["<", "group_role_definition_users.subscription_end", (new Date().toISOString())]},
            ]}
          />
        </div>
      </GenControlsBar>
      <GenTableDisplay
        {...groupRoleDefinitionUsers}
        columns={[
          {
            "title": "First Name",
            "sort_column": "users.first_name",
            "calculated_value": (item) => {
              return <a href={`group_role_definition_users/${item.id}`}>{item.user.first_name}</a>;
            },
          },{
            "title": "Last Name",
            "sort_column": "users.last_name",
            "calculated_value": (item) => {
              return <a href={`group_role_definition_users/${item.id}`}>{item.user.last_name}</a>;
            },
          },{
            "title": "Email",
            "column_width": "large",
            "sort_column": "users.email",
            "calculated_value": (item) => {
              return <a href={`group_role_definition_users/${item.id}`}>{item.user.email}</a>;
            },
          },{
            "title": "Role",
            "sort_column": "role_definitions.name",
            "calculated_value": (item) => {return item.role_definition.name;},
          },{
            "title": "Subscription Start",
            "sort_column": "group_role_definition_users.subscription_start",
            "calculated_value": (item) => {
              if (item.subscription_end === null){
                return("Invited");
              } else {
                if (item.subscription_start === null){
                  return("");
                } else {
                  const subscription_start_date = new Date(item.subscription_start);
                  return(subscription_start_date.toISOString().slice(0, 10));
                }
              }
            },
          },{
            "title": "Subscription End",
            "sort_column": "group_role_definition_users.subscription_end",
            "calculated_value": (item) => {
              if (item.subscription_end === null){
                return("Invited");
              } else {
                const subscription_end_date = new Date(item.subscription_end);
                if (subscription_end_date > new Date("2100")) {
                  return("Never Expires");
                } else if (subscription_end_date.getTime() > Date.now()) {
                  return(subscription_end_date.toISOString().slice(0, 10));
                } else {
                  return(`Expired: ${subscription_end_date.toISOString().slice(0, 10)}`);
                }
              }
            },
          },
        ]}
      />
      <GenMore
        {...groupRoleDefinitionUsers}
        shouldInfiniteScroll
      />
      {isExpirationModalOpen && (
        <GroupRoleDefinitionUserExpirationModal
          {...groupRoleDefinitionUsers}
          closeModal={() => setIsExpirationModalOpen(false)}
        />
      )}
    </div>
  );
}

export default connectToList(GroupRoleDefinitionUserTable, ["groupRoleDefinitionUsers"]);
